import * as React from 'react';

import { cn } from 'lib/utils';

export interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
  ariaInvalid?: boolean;
  className?: string;
}

export const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled,
  autoFocus,
  ariaLabel = 'Verification code',
  ariaInvalid,
  className,
}) => {
  const inputsRef = React.useRef<(HTMLInputElement | null)[]>([]);
  const lastNotifiedRef = React.useRef<string>('');

  // Fire onComplete exactly once when value reaches the target length.
  React.useEffect(() => {
    if (value.length === length && lastNotifiedRef.current !== value) {
      lastNotifiedRef.current = value;
      onComplete?.(value);
    } else if (value.length < length) {
      lastNotifiedRef.current = '';
    }
  }, [value, length, onComplete]);

  React.useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus();
    }
    // Only autofocus on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const charsAt = (i: number) => value[i] ?? '';

  const writeDigit = (index: number, digit: string) => {
    const chars = Array.from({ length }, (_, i) => value[i] ?? '');
    chars[index] = digit;
    // Trim trailing empty positions so the value stays a contiguous prefix.
    let endIdx = length;
    while (endIdx > 0 && chars[endIdx - 1] === '') endIdx--;
    onChange(chars.slice(0, endIdx).join(''));
  };

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit) return;
    writeDigit(index, digit);
    if (index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (charsAt(index)) {
        writeDigit(index, '');
      } else if (index > 0) {
        writeDigit(index - 1, '');
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, length - 1);
    inputsRef.current[focusIdx]?.focus();
  };

  return (
    <div role="group" aria-label={ariaLabel} className={cn('flex gap-2', className)}>
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => {
            inputsRef.current[idx] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={charsAt(idx)}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-label={`Digit ${idx + 1} of ${length}`}
          aria-invalid={ariaInvalid}
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          className={cn(
            'h-12 w-12 rounded-md border border-input bg-background text-center text-lg font-semibold ring-offset-background',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            ariaInvalid && 'border-destructive ring-destructive',
          )}
        />
      ))}
    </div>
  );
};

OtpInput.displayName = 'OtpInput';
